import { useState, useCallback, useRef, useEffect } from 'react';
import { ClienteFiltros, ClienteListItem } from '../types';
import { clienteService } from '../services/clienteService';

const LIMITE_DEFAULT = 10;

export type EstadoFilter = 'todos' | 'activos' | 'inactivos';

export interface ClienteMetrics {
  total: number;
  activos: number;
  nuevosEsteMes: number;
}

export function useClientes() {
  const [clientes, setClientes] = useState<ClienteListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [estadoFilter, setEstadoFilterState] = useState<EstadoFilter>('todos');
  const [metrics, setMetrics] = useState<ClienteMetrics>({ total: 0, activos: 0, nuevosEsteMes: 0 });

  const [filtros, setFiltros] = useState<ClienteFiltros>({
    pagina: 1,
    limite: LIMITE_DEFAULT,
    activo: undefined,
  });

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cargarResumen = useCallback(async () => {
    try {
      const resumen = await clienteService.resumen();
      setMetrics(resumen);
    } catch {
      // metrics are non-critical; fail silently
    }
  }, []);

  const cargar = useCallback(async (f: ClienteFiltros) => {
    try {
      setLoading(true);
      setError(null);
      const res = await clienteService.listar(f);
      setClientes(res.data);
      setTotal(res.total);
      setTotalPaginas(res.totalPaginas);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error cargando clientes');
    } finally {
      setLoading(false);
    }
  }, []);

  const aplicarFiltros = useCallback((nuevosFiltros: Partial<ClienteFiltros>) => {
    const updated = { ...filtros, ...nuevosFiltros, pagina: 1 };
    setFiltros(updated);
    cargar(updated);
  }, [filtros, cargar]);

  const cambiarPagina = useCallback((pagina: number) => {
    const updated = { ...filtros, pagina };
    setFiltros(updated);
    cargar(updated);
  }, [filtros, cargar]);

  const buscar = useCallback((busqueda: string) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      aplicarFiltros({ busqueda: busqueda || undefined });
    }, 300);
  }, [aplicarFiltros]);

  const cancelBusqueda = useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
  }, []);

  const setEstadoFilter = useCallback((estado: EstadoFilter) => {
    setEstadoFilterState(estado);
    const activo = estado === 'todos' ? undefined : estado === 'activos' ? true : false;
    aplicarFiltros({ activo });
  }, [aplicarFiltros]);

  const recargar = useCallback(async () => {
    await cargar(filtros);
    await cargarResumen();
  }, [cargar, filtros, cargarResumen]);

  const eliminar = useCallback(async (id: string) => {
    await clienteService.eliminar(id);
    await recargar();
  }, [recargar]);

  useEffect(() => {
    cargarResumen();
  }, [cargarResumen]);

  return {
    clientes,
    total,
    totalPaginas,
    loading,
    error,
    filtros,
    estadoFilter,
    setEstadoFilter,
    metrics,
    aplicarFiltros,
    cambiarPagina,
    buscar,
    cancelBusqueda,
    recargar,
    eliminar,
    cargar,
  };
}
